import { useState, useEffect } from 'react';
import { useTranslate, useNotify } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
  Slider,
  FormHelperText,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { ticketApi, type TicketSettings } from '../../../../services/api.service';

interface TicketSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const TIMEOUT_MARKS = [
  { value: 0, label: 'Off' },
  { value: 15, label: '15m' },
  { value: 30, label: '30m' },
  { value: 60, label: '1h' },
  { value: 120, label: '2h' },
  { value: 360, label: '6h' },
  { value: 720, label: '12h' },
  { value: 1440, label: '24h' },
];

/**
 * Dialog for managing ticket system settings.
 *
 * Settings available:
 * - Auto-reopen timeout: Time window after which closed tickets can be reopened
 *   when a customer sends a new message. 0 = disabled (always create new ticket).
 */
export function TicketSettingsDialog({ open, onClose }: TicketSettingsDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<TicketSettings | null>(null);
  const [autoReopenTimeout, setAutoReopenTimeout] = useState(30);

  // Load settings when dialog opens
  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ticketApi.getSettings();
      setSettings(response.data);
      setAutoReopenTimeout(response.data.autoReopenTimeoutMinutes);
    } catch (err) {
      setError(translate('resources.tickets.settings.error.load'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await ticketApi.updateSettings({
        autoReopenTimeoutMinutes: autoReopenTimeout,
      });
      setSettings(response.data);
      notify(translate('resources.tickets.settings.success.saved'), {
        type: 'success',
      });
      onClose();
    } catch (err) {
      setError(translate('resources.tickets.settings.error.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    setAutoReopenTimeout(newValue as number);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 1440) {
      setAutoReopenTimeout(value);
    }
  };

  const formatTimeoutLabel = (minutes: number): string => {
    if (minutes === 0) {
      return translate('resources.tickets.settings.autoReopen.disabled');
    }
    if (minutes < 60) {
      return translate('resources.tickets.settings.autoReopen.minutes', { minutes });
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return translate('resources.tickets.settings.autoReopen.hours', { hours });
    }
    return translate('resources.tickets.settings.autoReopen.hoursAndMinutes', {
      hours,
      minutes: remainingMinutes,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon />
        {translate('resources.tickets.settings.title')}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ pt: 1 }}>
            {/* Auto-Reopen Timeout Setting */}
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              {translate('resources.tickets.settings.autoReopen.title')}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate('resources.tickets.settings.autoReopen.description')}
            </Typography>

            <Box sx={{ px: 2, mb: 2 }}>
              <Slider
                value={autoReopenTimeout}
                onChange={handleSliderChange}
                min={0}
                max={1440}
                step={5}
                marks={TIMEOUT_MARKS}
                valueLabelDisplay="auto"
                valueLabelFormat={formatTimeoutLabel}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <TextField
                type="number"
                size="small"
                value={autoReopenTimeout}
                onChange={handleInputChange}
                inputProps={{ min: 0, max: 1440 }}
                sx={{ width: 100 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {translate('resources.tickets.settings.autoReopen.unit')}
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="body2" color="text.secondary">
                = {formatTimeoutLabel(autoReopenTimeout)}
              </Typography>
            </Box>

            <FormHelperText>
              {autoReopenTimeout === 0
                ? translate('resources.tickets.settings.autoReopen.hint.disabled')
                : translate('resources.tickets.settings.autoReopen.hint.enabled', {
                    time: formatTimeoutLabel(autoReopenTimeout),
                  })}
            </FormHelperText>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {translate('ra.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TicketSettingsDialog;
